import { useCallback, useSyncExternalStore } from 'react';

const dispatchStorageEvent = (key: string, newValue?: string | null) => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

const setLocalStorageItem = (key: string, value: unknown) => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: string) => window.localStorage.getItem(key);

const useLocalStorageSubscribe = (callback: (event: StorageEvent) => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

// This is a client-only hook
const getLocalStorageServerSnapshot = () => null;

/**
 * A hook to store, retrieve, and synchronize data from the browser’s localStorage API
 * Adapted from: https://github.com/uidotdev/usehooks & https://usehooks.com/uselocalstorage
 */
export function useLocalStorage<T>(key: string) {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = useCallback(
    (v: T) => {
      try {
        const nextState =
          typeof v === 'function' ? v(store ? JSON.parse(store) : store) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    },
    [key, store],
  );

  const safeParse = useCallback((data: string | null) => {
    if (data === null) return data;

    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }, []);

  return [store ? (safeParse(store) as T) : null, setState] as const;
}
